/**
 * DI-2-4 — the metadata round trip for ONE acquisition: build the wire objects,
 * post them in batches, fold the answers.
 *
 *   POST /data-acquisitions/{id}/data-points   (inspection-3/v1 2.1.0)
 *
 * Batches go out one after another (never in parallel — one acquisition's
 * batches share its filename and payload-hash keys). Every answer is folded
 * into one `RoundTripResult`:
 *
 *   created     rows the server inserted
 *   duplicates  items whose payload hash was already in the acquisition —
 *               answered with the existing point (`duplicateOf`, DI-D4 c1)
 *   replayed    items whose externalId already existed = length − created −
 *               duplicates (a re-drop: DI-D4 c4 — "the client is told so")
 *   links       refs with a non-empty `alsoIn` — the same image in another
 *               acquisition of this tenant (DI-D4 c3), for the review screen
 *
 * A refusal (4xx/5xx) stops the acquisition at that batch and carries the
 * envelope: a 422 `duplicate_payload_in_batch` cannot happen from this client
 * (DI-2-2 removed in-set duplicates) — if it does, it is shown, not retried.
 */
import type { Inspection3Components } from "@aeriosiq/types";
import {
  inspection3Fetch,
  isInspection3FetchError,
} from "../api/inspection3Fetch.js";
import {
  ROW_BATCH_DEADLINE_MS,
  deadlineWords,
  isRequestTimeout,
} from "../api/deadline.js";
import {
  toBatches,
  toDataPointInput,
  wireNames,
  type DataPointInput,
  type SyncRow,
} from "./toDataPointInput.js";

export type DataPointsCreated =
  Inspection3Components["schemas"]["DataPointsCreated"];

export interface RoundTripLink {
  externalId: string;
  dataPointId: string;
  imageFilename: string;
  alsoIn: string[];
}

export interface RoundTripResult {
  /** Items sent (= rows of the acquisition that had a hash). */
  sent: number;
  created: number;
  duplicates: number;
  replayed: number;
  links: RoundTripLink[];
  /** Every acquisition any link points at, once. */
  linkedAcquisitions: string[];
  /** Rows sent under a `~n` wire name (same name, different image). */
  renamed: number;
  /** The payload hashes of what was sent (the session keeps them). */
  payloadHashes: string[];
  at: string;
}

export interface RoundTripError {
  status: number | null;
  reason: string | null;
  message: string;
}

export type PostDataPoints = (
  dataAcquisitionId: string,
  dataPoints: DataPointInput[],
) => Promise<DataPointsCreated>;

export const postDataPoints: PostDataPoints = (dataAcquisitionId, dataPoints) =>
  inspection3Fetch<DataPointsCreated>(
    `/data-acquisitions/${encodeURIComponent(dataAcquisitionId)}/data-points`,
    // DI-3-S68 B1 — a row batch has its own (longer) deadline
    { method: "POST", body: { dataPoints }, timeoutMs: ROW_BATCH_DEADLINE_MS },
  );

export async function buildInputs(
  rows: readonly SyncRow[],
  dataAcquisitionId: string,
): Promise<{ items: DataPointInput[]; renamed: number }> {
  const names = wireNames(rows);
  let renamed = 0;
  const items: DataPointInput[] = [];
  for (const r of rows) {
    const wire = names.get(r.index) ?? r.name;
    if (wire !== r.name) renamed += 1;
    items.push(await toDataPointInput(r, dataAcquisitionId, wire));
  }
  return { items, renamed };
}

export function describeRoundTripError(e: unknown): RoundTripError {
  // DI-3-S68 B1 — a timeout is a transport failure, worded as one
  if (isRequestTimeout(e))
    return {
      status: null,
      reason: null,
      message: `The metadata could not be sent — the server ${deadlineWords(e)}. Retry.`,
    };
  if (!isInspection3FetchError(e)) {
    return {
      status: null,
      reason: null,
      message:
        "The metadata could not be sent — the server could not be reached. Try again.",
    };
  }
  const body = (e.body ?? {}) as { reason?: unknown; message?: unknown };
  const reason = typeof body.reason === "string" ? body.reason : null;
  const detail = typeof body.message === "string" ? body.message : null;
  const words: Record<string, string> = {
    duplicate_payload_in_batch:
      "Two files of one request are the same image (duplicate_payload_in_batch).",
    image_filename_conflict:
      "A file name already belongs to a different image in this data set (image_filename_conflict).",
  };
  const head =
    (reason && words[reason]) ??
    `The server refused the metadata (HTTP ${String(e.status)}${reason ? `, ${reason}` : ""}).`;
  return {
    status: e.status,
    reason,
    message: detail ? `${head} ${detail}` : head,
  };
}

export class RoundTripFailure extends Error {
  constructor(
    readonly detail: RoundTripError,
    readonly partial: RoundTripResult,
  ) {
    super(detail.message);
  }
}

/**
 * Send one acquisition's rows. `onProgress(sent, total)` after every batch.
 * Throws `RoundTripFailure` (with what was folded so far) on a refusal.
 */
export async function syncAcquisition({
  dataAcquisitionId,
  rows,
  post = postDataPoints,
  onProgress,
  now = () => new Date(),
}: {
  dataAcquisitionId: string;
  rows: readonly SyncRow[];
  post?: PostDataPoints;
  onProgress?: (sent: number, total: number) => void;
  now?: () => Date;
}): Promise<RoundTripResult> {
  const { items, renamed } = await buildInputs(rows, dataAcquisitionId);
  const nameOf = new Map(items.map((it) => [it.externalId, it.imageFilename]));
  const result: RoundTripResult = {
    sent: 0,
    created: 0,
    duplicates: 0,
    replayed: 0,
    links: [],
    linkedAcquisitions: [],
    renamed,
    payloadHashes: rows.flatMap((r) =>
      r.hashes.payloadSha256Hex ? [r.hashes.payloadSha256Hex] : [],
    ),
    at: now().toISOString(),
  };
  const linked = new Set<string>();
  onProgress?.(0, items.length);
  for (const batch of toBatches(items)) {
    let res: DataPointsCreated;
    try {
      res = await post(dataAcquisitionId, batch);
    } catch (e) {
      throw new RoundTripFailure(describeRoundTripError(e), {
        ...result,
        linkedAcquisitions: [...linked],
      });
    }
    const duplicates = res.duplicates;
    result.sent += batch.length;
    result.created += res.created;
    result.duplicates += duplicates;
    result.replayed += res.dataPoints.length - res.created - duplicates;
    for (const ref of res.dataPoints) {
      const alsoIn = ref.alsoIn ?? [];
      if (alsoIn.length === 0) continue;
      result.links.push({
        externalId: ref.externalId,
        dataPointId: ref.dataPointId,
        imageFilename: nameOf.get(ref.externalId) ?? "",
        alsoIn,
      });
      for (const a of alsoIn) linked.add(a);
    }
    onProgress?.(result.sent, items.length);
  }
  result.linkedAcquisitions = [...linked].sort();
  return result;
}

/**
 * The summary line (story §2.4). A session in which nothing new was created
 * and at least one item was replayed reads, verbatim, "527 already indexed —
 * 0 new" (M2a).
 */
export function summaryLine(
  r: Pick<
    RoundTripResult,
    | "sent"
    | "created"
    | "duplicates"
    | "replayed"
    | "linkedAcquisitions"
    | "links"
  >,
): string {
  if (r.created === 0 && r.replayed > 0) {
    return `${String(r.replayed)} already indexed — 0 new`;
  }
  const also = r.links.length;
  return `${String(r.created)} indexed · ${String(r.duplicates)} ${r.duplicates === 1 ? "duplicate" : "duplicates"} · ${String(also)} also in other acquisitions`;
}
